<template>
  <VueFinalModal
    :click-to-close="true"
    :esc-to-close="true"
    :focus-trap="modalFocusTrap"
    class="final-modal"
    overlay-transition="vfm-fade"
    :class="{
      'theme--default': theme === 'light',
      'theme--dark': theme === 'dark',
    }"
  >
    <template
      #default="{
        // @ts-ignore - vue-final-modal exposes `close` through slot
        close,
      }"
    >
      <div
        class="content"
        aria-modal="true"
        role="dialog"
        aria-labelledby="createAgentTitle"
        tabindex="-1"
      >
        <div class="header">
          <h4 id="createAgentTitle" class="title">New agent</h4>
          <BaseButton kind="auxiliary" class="close-icon" @click="close">
            <template #icon>
              <CloseIcon />
            </template>
          </BaseButton>
        </div>

        <div class="body">
          <div class="field">
            <label class="field-label" for="agent-name-input">Name</label>
            <FormInput
              id="agent-name-input"
              ref="inputRef"
              :value="agentName"
              type="text"
              placeholder="e.g. Stella"
              @update:value="(v) => (agentName = v)"
              @keydown="(e) => onKeydown(e, close)"
            />
          </div>

          <div class="field">
            <label class="field-label">Avatar</label>
            <AgentAvatarPicker v-model="selectedAvatar" :columns="4" />
          </div>
        </div>

        <div class="footer">
          <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>
          <div class="buttons">
            <BaseButton
              kind="main"
              type="tertiary"
              text="Cancel"
              :state="isCreating ? 'disabled' : 'default'"
              @click="close"
            />
            <BaseButton
              kind="main"
              type="primary"
              text="Create"
              :state="createButtonState"
              @click="onCreate(close)"
            />
          </div>
        </div>
      </div>
    </template>
  </VueFinalModal>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  nextTick,
  onMounted,
  PropType,
  ref,
} from "vue";
import { VueFinalModal } from "vue-final-modal";

import BaseButton from "@/components/base/BaseButton.vue";
import FormInput from "@/components/graph/definition/form/FormInput.vue";
import CloseIcon from "@/components/icons/CloseIcon.vue";
import * as inject from "@/components/inject";
import { modalFocusTrap } from "@/components/modals/focus-trap";

import { AVATAR_KEYS } from "./AgentAvatar.vue";
import AgentAvatarPicker from "./AgentAvatarPicker.vue";

export default defineComponent({
  name: "CreateAgentModal",
  components: {
    AgentAvatarPicker,
    BaseButton,
    CloseIcon,
    FormInput,
    VueFinalModal,
  },
  props: {
    onConfirm: {
      type: Function as PropType<
        (name: string, avatarKey: string) => Promise<void>
      >,
      required: true,
    },
  },
  setup(props) {
    const theme = inject.theme();
    const inputRef = ref<InstanceType<typeof FormInput> | null>(null);

    const agentName = ref("");
    const selectedAvatar = ref<string>(AVATAR_KEYS[0]);
    const isCreating = ref(false);
    const errorMessage = ref<string | null>(null);

    const trimmedName = computed(() => agentName.value.trim());

    const createButtonState = computed(() => {
      if (isCreating.value) return "loading";
      return trimmedName.value ? "default" : "disabled";
    });

    const onCreate = (close: () => void) => {
      if (!trimmedName.value || isCreating.value) return;
      isCreating.value = true;
      errorMessage.value = null;
      props
        .onConfirm(trimmedName.value, selectedAvatar.value)
        .then(() => {
          close();
        })
        .catch((err: unknown) => {
          errorMessage.value =
            err instanceof Error ? err.message : "Something went wrong.";
        })
        .finally(() => {
          isCreating.value = false;
        });
    };

    const onKeydown = (event: KeyboardEvent, close: () => void) => {
      if (event.key === "Enter" && createButtonState.value === "default") {
        onCreate(close);
      }
    };

    onMounted(() => {
      void nextTick(() => {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            inputRef.value?.focus();
          });
        });
      });
    });

    return {
      modalFocusTrap,
      theme,
      inputRef,
      agentName,
      selectedAvatar,
      isCreating,
      errorMessage,
      createButtonState,
      onCreate,
      onKeydown,
    };
  },
});
</script>

<style scoped lang="scss">
@use "@/styles/_variables.scss" as *;
@use "@/styles/_themes.scss" as *;

.content {
  display: flex;
  flex-direction: column;
  border-radius: $border-radius-l;
  width: 520px;
  box-shadow: 0px 1px 32px 0px rgba(0, 0, 0, 0.12);

  @include themed() {
    background-color: t($bg);
    color: t($primary);
  }
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px 12px;

  @include themed() {
    border-bottom: 1px solid t($grey-15);
  }
}

.title {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
}

.body {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px 16px 24px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field-label {
  @extend %font-caption-regular;
  @include themed() {
    color: t($grey-70);
  }
}

.footer {
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;

  @include themed() {
    border-top: 1px solid t($grey-10);
  }

  .buttons {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
  }
}

.error-message {
  @extend %font-caption-regular;
  margin: 0;

  @include themed() {
    color: t($secondary-50);
  }
}
</style>
